import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AgGridModule, ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

import {
	PrismDialogService,
	PrismIconButtonComponent,
	PrismIconComponent,
	PrismProgressIndicatorComponent,
	PrismTooltipComponent
} from '@shure/prism-angular-components';
import {
	DeviceErrorType,
	DeviceLicense,
	DeviceLicenseState,
	DeviceLicenseWarningDetails,
	DeviceStatus,
	MissingFeatureType
} from '@shure/shared/angular/data-access/system-api/models';
import { DeviceLicenseStateIconComponent } from '@shure/shared/angular/features/device-license/feature';
import { PrismCommonDialogsService } from '@shure/shared/angular/ui/components/dialogs/common';
import { DiscoveryErrorDialogComponent } from '@shure/shared/angular/ui/components/dialogs/discovery-error-dialog';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';

import { OnlineDevicesView } from './models/device-status';

export interface DeviceStatusRendererDevice {
	status: DeviceStatus;
	deviceError: DeviceErrorType;
	statusCellData: {
		deviceErrorTooltip: string;
		filterText: string;
		selectedView: OnlineDevicesView;
		isTransmitterDevice: boolean;
	};
	features: {
		license?: DeviceLicense<DeviceLicenseWarningDetails> & MissingFeatureType;
	};
}

export interface DeviceStatusRendererParentComponent {
	onNewDeviceButtonClick: (event: Event, device: DeviceStatusRendererDevice) => void;
}

interface DeviceStatusRendererComponentContext {
	componentParent: DeviceStatusRendererParentComponent;
	selectedView: OnlineDevicesView;
}

export type DeviceStatusRendererComponentParamsType = ICellRendererParams<
	DeviceStatusRendererDevice,
	unknown,
	DeviceStatusRendererComponentContext
>;

/**
 * ```sh-device-status-renderer``` is a view component for displaying device status component, such as warning and error icons
 */
@Component({
	selector: 'sh-device-status-renderer',
	templateUrl: './device-status-renderer.component.html',
	styleUrls: ['./device-status-renderer.component.scss'],
	imports: [
		CommonModule,
		AgGridModule,
		TranslocoModule,
		PrismIconComponent,
		PrismTooltipComponent,
		PrismIconButtonComponent,
		PrismProgressIndicatorComponent,
		DeviceLicenseStateIconComponent
	]
})
@shUseTranslation('shAgGrid', /* istanbul ignore next */ (lang) => import(`../../../i18n/${lang}`))
export class DeviceStatusRendererComponent implements ICellRendererAngularComp {
	public device!: DeviceStatusRendererDevice | undefined;
	public groupDevice!: DeviceStatusRendererDevice | undefined;
	public params!: DeviceStatusRendererComponentParamsType;

	public groupedByOtherColumn = false;

	public deviceStatus = DeviceStatus;
	public deviceError: DeviceErrorType | undefined;
	public deviceErrorTooltip = '';
	public selectedView!: OnlineDevicesView;
	public isTransmitterDevice!: boolean;

	protected deviceLicenseState = DeviceLicenseState;
	protected deviceErrorType = DeviceErrorType;
	protected onlineDevicesView = OnlineDevicesView;

	constructor(
		private readonly translocoService: TranslocoService,
		private readonly commonDialogsService: PrismCommonDialogsService,
		private readonly dialogService: PrismDialogService
	) {}

	public agInit(params: DeviceStatusRendererComponentParamsType): void {
		if (!params) {
			return;
		}
		this.update(params);
	}

	/* istanbul ignore next */
	public refresh(params: DeviceStatusRendererComponentParamsType): boolean {
		if (!params) {
			return true;
		}
		this.update(params);
		return true;
	}

	public onNewDeviceButtonClick(event: Event, device: DeviceStatusRendererDevice): void {
		this.params.context.componentParent.onNewDeviceButtonClick(event, device);
	}

	public onErrorIconClick = (event: Event): void => {
		event.preventDefault();
		event.stopPropagation();
		this.dialogService.openDialog<DiscoveryErrorDialogComponent, unknown, unknown>(DiscoveryErrorDialogComponent);
	};

	public onPoeIconClick = (event: Event): void => {
		event.preventDefault();
		event.stopPropagation();
	};

	public onDanteLockedIconClick = (event: Event): void => {
		event.preventDefault();
		event.stopPropagation();
		this.commonDialogsService.openInfoDialog({
			title: this.translocoService.translate('shAgGrid.common.errors.dante-locked-banner-title'),
			body: [
				this.translocoService.translate('shAgGrid.common.errors.dante-locked-banner-content-1'),
				this.translocoService.translate('shAgGrid.common.errors.dante-locked-banner-content-2'),
				this.translocoService.translate('shAgGrid.common.errors.dante-locked-banner-content-3')
			],
			okLabel: this.translocoService.translate('shAgGrid.common.buttons.ok')
		});
	};

	private update(params: DeviceStatusRendererComponentParamsType): void {
		this.device = params.data;
		this.selectedView = params.context?.selectedView;

		if (params.node.group && params.node.field) {
			if (params.node.field === 'statusCellData') {
				const groupDevice = params.node.allLeafChildren?.length
					? params.node.allLeafChildren[0].data
					: undefined;
				this.groupDevice =
					this.selectedView === OnlineDevicesView.Rooms || this.selectedView === OnlineDevicesView.Connections
						? undefined
						: groupDevice;
			} else {
				this.groupedByOtherColumn = true;
			}
		}

		this.params = params;

		this.deviceError = params.data?.deviceError;
		this.deviceErrorTooltip = params.data?.statusCellData?.deviceErrorTooltip ?? '';
		this.isTransmitterDevice = params.data?.statusCellData.isTransmitterDevice ?? false;
	}
}
